import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormDialog, Input, Label } from '@rentflowdev/ui'
import { useUpdateDocumentMutation } from '@/api'
import { extractErrorMessage } from '@/lib/error-message'

interface RenameDocumentDialogProps {
	document: { id: string; title: string }
	onOpenChange: (open: boolean) => void
}

/**
 * Renaming a document, shared by the property, deal and rental lists.
 *
 * A generated document is named after its template and an uploaded one after
 * the file on disk, so a list ends up holding «Dogovor_arendy_forma.doc» next
 * to «Создание договора аренды здания» — neither of which says which document
 * it is. The name was the one thing about a document that could not be
 * corrected after the fact.
 *
 * Mounted by the caller only while open, so the field starts from the saved
 * title on every open rather than being reset from an effect.
 */
export function RenameDocumentDialog({ document, onOpenChange }: RenameDocumentDialogProps) {
	const { t } = useTranslation()
	const [updateDocument, { isLoading }] = useUpdateDocumentMutation()
	const [title, setTitle] = useState(document.title)
	const [error, setError] = useState<string | null>(null)

	const trimmed = title.trim()
	// An empty name is worse than the generated one it would replace, and
	// saving an unchanged title is a request that asks for nothing.
	const canSubmit = trimmed.length > 0 && trimmed !== document.title

	async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault()
		if (!canSubmit) return
		setError(null)
		try {
			await updateDocument({ id: document.id, body: { title: trimmed } }).unwrap()
			onOpenChange(false)
		} catch (err) {
			setError(extractErrorMessage(err) ?? t('documents.rename.error'))
		}
	}

	return (
		<FormDialog
			open
			onOpenChange={onOpenChange}
			title={t('documents.rename.title')}
			onSubmit={handleSubmit}
			submitLabel={t('common.save')}
			cancelLabel={t('common.cancel')}
			isSubmitting={isLoading}
			isSubmitDisabled={!canSubmit}
		>
			<div className="flex flex-col gap-2">
				<Label htmlFor="document-title">{t('documents.rename.label')}</Label>
				<Input
					id="document-title"
					value={title}
					autoFocus
					onChange={(e) => setTitle(e.target.value)}
					placeholder={t('documents.rename.placeholder')}
				/>
				{error && <p className="text-sm text-(--color-destructive)">{error}</p>}
			</div>
		</FormDialog>
	)
}
